import React, { useState, useEffect, useRef } from 'react';

const SelectorVariantesModal = ({ item, onClose, onConfirm, editando = false, layoutHorizontal = false }) => {
    const [opcionesSeleccionadas, setOpcionesSeleccionadas] = useState([]);
    const [precioTotal, setPrecioTotal] = useState(0);
    const scrollContainerRef = useRef(null);
    const groupRefs = useRef({});
    const bottomModalRef = useRef(null);

    // Función helper para obtener nombre de opción (soporta nombre_opcion del web)
    const getNombreOpcion = (o) => o.nombre || o.nombre_opcion || "Opción";
    
    // Función helper para evaluar "seleccionado_por_defecto" (true, 1, "1")
    const esDefault = (opcion) => {
        return opcion.seleccionado_por_defecto === true || opcion.seleccionado_por_defecto === 1 || opcion.seleccionado_por_defecto === "1";
    };

    // 🚩 Auto-scroll al siguiente grupo (reutilizable)
    const scrollAlSiguienteGrupo = (grupoActual) => {
        const todos = item.variantes || item.grupos || [];
        const idx = todos.findIndex(group => group.id === grupoActual.id);
        const sig = todos[idx + 1];
        if (sig && groupRefs.current[sig.id]) {
            setTimeout(() => groupRefs.current[sig.id].scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
        }
    };

    // 🚩 LÓGICA MEJORADA: Soporta edición de items del carro
    useEffect(() => {
        if (!item) return;

        let predefinidos = [];
        const grupos = item.variantes || item.grupos || [];

        // 🚩 Si el item viene del carro (tiene opciones_escogidas), precargamos esas
        if (item.opciones_escogidas && item.opciones_escogidas.length > 0) {
            predefinidos = [...item.opciones_escogidas];
        } else {
            // Si es nuevo, solo lo que marcaste en el panel admin como "por defecto"
            grupos.forEach(g => {
                if (g.opciones) {
                    g.opciones.forEach(o => {
                        if (esDefault(o)) {
                            predefinidos.push(o);
                        }
                    });
                }
            });
        }

        setOpcionesSeleccionadas(predefinidos);
        const pBase = Number(item.precio || item.valor || 0);
        const totalAjustes = predefinidos.reduce((acc, curr) => acc + Number(curr.ajuste_precio || curr.precio || 0), 0);
        setPrecioTotal(pBase + totalAjustes);
    }, [item]);

    const toggleOpcion = (g, o) => {
        const tipoStr = String(g.tipo || g.tipo_seleccion || "").toLowerCase();
        const esUnica = tipoStr.includes('unic') || tipoStr.includes('única');
        const esObligatoria = tipoStr.includes('oblig') || tipoStr.includes('obligatoria');

        const estaSeleccionada = opcionesSeleccionadas.find(x => x.id === o.id);
        let nuevasSelecciones;

        if (esUnica) {
            if (estaSeleccionada && esObligatoria) return;
            if (estaSeleccionada) {
                nuevasSelecciones = opcionesSeleccionadas.filter(x => x.id !== o.id);
            } else {
                const idsGrupo = g.opciones.map(x => x.id);
                const filtradas = opcionesSeleccionadas.filter(x => !idsGrupo.includes(x.id));
                nuevasSelecciones = [...filtradas, o];
                scrollAlSiguienteGrupo(g);
            }
        } else {
            // Selección múltiple
            if (estaSeleccionada) {
                nuevasSelecciones = opcionesSeleccionadas.filter(x => x.id !== o.id);
            } else {
                const max = Number(g.max_seleccion || 0);
                if (max > 0) {
                    const deEsteGrupo = opcionesSeleccionadas.filter(sel => g.opciones.some(opt => opt.id === sel.id)).length;
                    if (deEsteGrupo >= max) { alert(`Máximo ${max} opciones`); return; }
                }
                nuevasSelecciones = [...opcionesSeleccionadas, o];
                // 🚩 Auto-scroll al siguiente grupo si se completó el máximo
                if (max > 0) {
                    const ahoraDeEsteGrupo = (nuevasSelecciones.filter(sel => g.opciones.some(opt => opt.id === sel.id))).length;
                    if (ahoraDeEsteGrupo >= max) {
                        scrollAlSiguienteGrupo(g);
                    }
                }
            }
        }

        setOpcionesSeleccionadas(nuevasSelecciones);
        const pBase = Number(item.precio || item.valor || 0);
        const totalAjustes = nuevasSelecciones.reduce((acc, curr) => acc + Number(curr.ajuste_precio || curr.precio || 0), 0);
        setPrecioTotal(pBase + totalAjustes);
    };

    const handleConfirmar = () => {
        const grupos = item.variantes || item.grupos || [];
        for (let g of grupos) {
            const strTipo = String(g.tipo || g.tipo_seleccion || "").toLowerCase();
            if ((strTipo.includes('unic') || strTipo.includes('única')) && (strTipo.includes('oblig') || strTipo.includes('obligatoria'))) {
                if (!opcionesSeleccionadas.some(sel => g.opciones.some(opt => String(opt.id) === String(sel.id)))) {
                    // 🚩 Scroll al grupo que falta y alerta más amigable
                    if (groupRefs.current[g.id]) {
                        groupRefs.current[g.id].scrollIntoView({ behavior: 'smooth', block: 'start' });
                        // Resaltar el grupo con un borde rojo temporal
                        groupRefs.current[g.id].style.border = '2px solid #dc3545';
                        setTimeout(() => {
                            if (groupRefs.current[g.id]) groupRefs.current[g.id].style.border = 'none';
                        }, 2000);
                    }
                    alert(`⚠️ Debes elegir una opción en: ${(g.nombre_grupo || g.titulo).toUpperCase()}`);
                    return;
                }
            }
        }

        const notas = [];
        grupos.forEach(grupo => {
            const strTipo = String(grupo.tipo || grupo.tipo_seleccion || "").toLowerCase();
            const nombreGrupo = grupo.nombre_grupo || grupo.titulo || "OPCIONES";
            
            if (strTipo.includes('unic') || strTipo.includes('única')) {
                const sel = opcionesSeleccionadas.find(s => grupo.opciones.some(go => String(go.id) === String(s.id)));
                if (sel) notas.push(`[${nombreGrupo}]: ${getNombreOpcion(sel)}`);
            } else {
                const q = grupo.opciones.filter(o => esDefault(o) && !opcionesSeleccionadas.some(s => s.id === o.id));
                const a = opcionesSeleccionadas.filter(s => grupo.opciones.some(go => go.id === s.id) && !esDefault(s));
                let txt = [];
                if (q.length > 0) txt.push("SIN: " + q.map(i => getNombreOpcion(i)).join(', '));
                if (a.length > 0) {
                    // 🚩 Lógica especial para SALSAS (como en PedidosWeb)
                    const nombreG = nombreGrupo.toUpperCase();
                    const prefijo = nombreG.includes("SALSA") ? "" : "EXTRA: ";
                    txt.push(prefijo + a.map(i => getNombreOpcion(i)).join(', '));
                }
                if (txt.length > 0) notas.push(`[${nombreGrupo}]: ${txt.join(' ')}`);
            }
        });

        // 🚩 Si estamos editando, preservamos el ID original para poder reemplazar en el carro
        const itemFinal = {
            ...item,
            precio: precioTotal,
            cantidad: 1,
            variantes_detalles: notas.join(' | '),
            descripcionVariantes: notas.join(' | '), // Respaldo para catálogo web
            opciones_escogidas: opcionesSeleccionadas
        };

        // Solo generamos nuevo ID si NO es edición
        if (!editando) {
            itemFinal.id = `${item.id}_${Date.now()}`;
        }

        onConfirm(itemFinal);
    };

    return (
        <div className="position-fixed w-100 h-100 d-flex justify-content-center align-items-center" style={{ zIndex: 99999, top: 0, left: 0, backgroundColor: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(5px)' }}>
            <div className="position-relative" style={{ width: layoutHorizontal ? '80vw' : '650px', maxWidth: '95vw' }}>
                
                {/* Botones laterales solo si hay mucho contenido */}
                {(item.variantes || item.grupos || []).length > 2 && (
                    <div className="position-absolute d-none d-md-flex flex-column" style={{ right: '-80px', top: '50%', transform: 'translateY(-50%)', gap: '20px' }}>
                        <button className="btn btn-primary rounded-circle shadow-lg" style={{width: '60px', height: '60px'}} onClick={() => scrollContainerRef.current.scrollTo({top: 0, behavior: 'smooth'})}><i className="fas fa-chevron-up"></i></button>
                        <button className="btn btn-success rounded-circle shadow-lg" style={{width: '60px', height: '60px'}} onClick={() => bottomModalRef.current.scrollIntoView({behavior: 'smooth'})}><i className="fas fa-chevron-down"></i></button>
                    </div>
                )}

                <div className="bg-white shadow-2xl" style={{ display: 'flex', flexDirection: 'column', borderRadius: '25px', overflow: 'hidden', border: '1px solid #444' }}>
                    <div className="bg-dark p-4 text-white d-flex justify-content-between align-items-center">
                        <h4 className="m-0 font-weight-bold">{item.nombre}</h4>
                        <button className="btn btn-outline-light rounded-pill px-3 font-weight-bold" onClick={onClose}>X CERRAR</button>
                    </div>

                    <div ref={scrollContainerRef} className={`p-4 overflow-auto bg-light ${layoutHorizontal ? '' : ''}`} style={{ flex: 1, maxHeight: layoutHorizontal ? '68vh' : '60vh' }}>
                        {layoutHorizontal ? (
                            <div className="row">
                                {(item.variantes || item.grupos || []).map((g, index) => (
                                    <div key={g.id} className="col-md-6 col-lg-4 mb-4">
                                        <div ref={el => groupRefs.current[g.id] = el} className="card h-100 border-0 shadow-sm" style={{ borderRadius: '15px' }}>
                                            <div className="card-header border-0 bg-white d-flex justify-content-between align-items-center pt-3">
                                                <strong className="text-uppercase">{index + 1}. {g.nombre_grupo || g.titulo}</strong>
                                                {(String(g.tipo || g.tipo_seleccion || "").toLowerCase().includes('oblig') || String(g.tipo || g.tipo_seleccion || "").toLowerCase().includes('obligatoria')) && <span className="badge badge-danger">OBLIGATORIO</span>}
                                            </div>
                                            <div className="card-body">
                                                {g.opciones.map(o => {
                                                    const sel = opcionesSeleccionadas.some(s => s.id === o.id);
                                                    return (
                                                        <div key={o.id} className="mb-2" onClick={() => toggleOpcion(g, o)}>
                                                            <div className={`p-3 rounded-xl border d-flex align-items-center transition-all cursor-pointer ${sel ? 'bg-primary text-white shadow' : 'bg-white border'}`} style={{ borderRadius: '12px' }}>
                                                                <div className="mr-3">{sel ? <i className="fas fa-check-circle"></i> : <i className="far fa-circle text-muted"></i>}</div>
                                                                <div className="flex-grow-1 font-weight-bold">{o.nombre}</div>
                                                                {Number(o.precio || o.ajuste_precio) > 0 && <span>+ ${Number(o.precio || o.ajuste_precio).toLocaleString()}</span>}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            (item.variantes || item.grupos || []).map((g, index) => (
                                <div key={g.id} ref={el => groupRefs.current[g.id] = el} className="card mb-4 border-0 shadow-sm" style={{ borderRadius: '15px' }}>
                                    <div className="card-header border-0 bg-white d-flex justify-content-between align-items-center pt-3">
                                        <strong className="text-uppercase">{index + 1}. {g.nombre_grupo || g.titulo}</strong>
                                        {(String(g.tipo || g.tipo_seleccion || "").toLowerCase().includes('oblig') || String(g.tipo || g.tipo_seleccion || "").toLowerCase().includes('obligatoria')) && <span className="badge badge-danger">OBLIGATORIO</span>}
                                    </div>
                                    <div className="card-body">
                                        <div className="row">
                                            {g.opciones.map(o => {
                                                const sel = opcionesSeleccionadas.some(s => s.id === o.id);
                                                return (
                                                    <div key={o.id} className="col-12 mb-2" onClick={() => toggleOpcion(g, o)}>
                                                        <div className={`p-3 rounded-xl border d-flex align-items-center transition-all cursor-pointer ${sel ? 'bg-primary text-white shadow' : 'bg-white border'}`} style={{ borderRadius: '12px' }}>
                                                            <div className="mr-3">{sel ? <i className="fas fa-check-circle"></i> : <i className="far fa-circle text-muted"></i>}</div>
                                                            <div className="flex-grow-1 font-weight-bold">{o.nombre}</div>
                                                            {Number(o.precio || o.ajuste_precio) > 0 && <span>+ ${Number(o.precio || o.ajuste_precio).toLocaleString()}</span>}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}
                        <div ref={bottomModalRef}></div>
                    </div>

                    <div className="p-4 bg-white border-top d-flex justify-content-between align-items-center shadow-lg">
                        <h3 className="m-0 font-weight-bold text-primary">${precioTotal.toLocaleString()}</h3>
                        <button className="btn btn-success px-5 py-3 font-weight-bold rounded-pill" onClick={handleConfirmar}>
                            {editando ? 'ACTUALIZAR PEDIDO' : 'AGREGAR AL PEDIDO'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SelectorVariantesModal;